/**
 * /app/clients — wired against `list-clients` (read) and `create-client`
 * (write, Task 043).
 *
 * Leadership sees every client in their org; non-leadership sees only
 * their assignments. Sysadmin reads collapse to 404 (the endpoint
 * deliberately does not surface sysadmin to tenant routes per Task 024).
 *
 * Create flow (Task 043, extended Task 112D): an "إضافة عميل" CTA
 * opens an inline form panel; the empty state surfaces a stronger
 * primary CTA. Submission calls `createClient` which goes through the
 * agency-only `client.create` permission gate (RLS-enforced server-
 * side; the frontend hides the CTA for actors who lack the permission
 * as a UX affordance only). Task 112D requires a client portal email
 * at create time and immediately fires `sendUserInvitation` against
 * the new client + a resolved client-category role (preferring
 * `client_admin`, falling back to `client_manager`). The page-level
 * `InviteResultBanner` reports whether the invitation email was
 * actually delivered (`email_delivery: 'sent'`) or whether the
 * operator must copy the one-time link manually
 * (`'not_configured'` / `'failed'`). Existing rows expose a
 * "دعوة مستخدم عميل" action that opens the same flow scoped to a
 * single client.
 *
 * No native browser dialogs (`window.confirm` / `prompt` / `alert`)
 * anywhere on this page.
 */

import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { isApiError, KmiApiError } from '../api/client';
import {
  createClient as createClientApi,
  listClients,
  listRoles,
  sendUserInvitation,
} from '../api/endpoints';
import type {
  ClientRow,
  ClientStatus,
  InvitationEmailDelivery,
  RoleRow,
  SendUserInvitationData,
} from '../api/types';
import { useClientScope } from '../context/ClientScopeProvider';
import { useMeContext } from '../context/MeContextProvider';
import { hasAnyPermission, isAgencyProductionOnly, PERM } from '../permissions';
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '../components/states';

type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly clients: ReadonlyArray<ClientRow> }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

const STATUS_LABEL: Record<ClientStatus, string> = {
  onboarding: 'تأهيل',
  active: 'نشط',
  paused: 'متوقف',
  archived: 'مؤرشف',
};

const STATUS_PILL: Record<ClientStatus, string> = {
  onboarding: 'pill pill-info',
  active: 'pill pill-active',
  paused: 'pill pill-paused',
  archived: 'pill pill-archived',
};

// Task 112D — strict but cheap email shape check, mirrored from the
// backend regex in `supabase/functions/send-user-invitation/index.ts`.
// The server is the load-bearing validator; this is a UX guard that
// keeps the operator from firing a request the server will reject.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Task 112D — preferred client-category role keys for invitations. The
// agency operator does not pick the role manually — we resolve it from
// the available role set so a fresh org can invite portal users
// without a role-management detour. `client_admin` is the default
// portal role because it carries `contract.read`, which lets client
// users see their active package and unlocked workspace tabs.
// `client_manager` remains a narrower fallback for orgs that have not
// seeded `client_admin`.
const CLIENT_INVITE_ROLE_PRIORITY = ['client_admin', 'client_manager'] as const;

function clientInitials(name: string): string {
  const parts = name
    .split(/\s+/)
    .filter((p) => p.length > 0)
    .slice(0, 2);
  if (parts.length === 0) return name.slice(0, 2);
  return parts.map((p) => p.charAt(0)).join('.');
}

function resolveDefaultClientRole(roles: ReadonlyArray<RoleRow>): RoleRow | null {
  for (const key of CLIENT_INVITE_ROLE_PRIORITY) {
    const match = roles.find((r) => r.category === 'client' && r.is_active && r.key === key);
    if (match) return match;
  }
  return null;
}

// Task 112D — construct the same accept-invitation URL the backend
// embeds in the Resend email so the operator can copy/paste it when
// email delivery is not configured / failed.
// `AcceptInvitationPage` reads `?token=&email=` from the query string.
function buildAcceptInvitationUrl(rawToken: string, email: string): string {
  const url = new URL('/accept-invitation', window.location.origin);
  url.searchParams.set('token', rawToken);
  url.searchParams.set('email', email);
  return url.toString();
}

interface InviteResultState {
  readonly clientName: string;
  readonly email: string;
  readonly delivery: InvitationEmailDelivery;
  readonly acceptUrl: string;
}

function inviteResultFromResponse(
  resp: SendUserInvitationData,
  clientName: string,
  email: string,
): InviteResultState {
  return {
    clientName,
    email,
    delivery: resp.email_delivery,
    acceptUrl: buildAcceptInvitationUrl(resp.raw_token, email),
  };
}

export function ClientsListPage() {
  const { me } = useMeContext();
  const clientScope = useClientScope();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [filter, setFilter] = useState<ClientStatus | 'all'>('all');
  const [reloadKey, setReloadKey] = useState(0);
  const [showForm, setShowForm] = useState(() => params.get('new') === '1');
  // Task 112D — page-level invite-result banner (shared by both the
  // create-client flow and the per-row "دعوة مستخدم عميل" modal).
  const [inviteResult, setInviteResult] = useState<InviteResultState | null>(null);
  // Task 112D — per-row invite modal state.
  const [inviteModalClient, setInviteModalClient] = useState<ClientRow | null>(null);
  // Task 112D — client-category roles fetched once on mount; used to
  // resolve the default invitation role for both flows.
  const [clientRoles, setClientRoles] = useState<ReadonlyArray<RoleRow> | null>(null);
  const [clientRolesError, setClientRolesError] = useState<string | null>(null);

  const canCreate =
    !!me && !me.system_admin && hasAnyPermission(me.permissions, PERM.CLIENT_CREATE);
  const productionWorkspace = me ? isAgencyProductionOnly(me) : false;

  // Task hide-archived-clients — refetch when the operator toggles the
  // "مؤرشف" chip. The default fetch (filter !== 'archived') excludes
  // archived rows server-side; the `archived` chip explicitly opts in
  // via `?include_archived=true`. Both fetches return the same wire
  // shape, but the `archived` payload also contains the archived rows
  // so `ClientsGrid` can client-side filter to `c.status === 'archived'`.
  useEffect(() => {
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    const includeArchived = filter === 'archived';
    listClients({ signal: ctrl.signal, includeArchived })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'ready', clients: data.clients });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setState({ status: 'not-found' });
          return;
        }
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [reloadKey, filter]);

  // Task 112D — fetch client-category roles once so both invite flows
  // can resolve `client_manager` / `client_admin` without re-hitting
  // `list-roles` per submit. Sysadmin returns above so this only
  // fires for tenant actors.
  useEffect(() => {
    if (!me || me.system_admin) return;
    const ctrl = new AbortController();
    listRoles({ signal: ctrl.signal, category: 'client' })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setClientRoles(data.roles);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setClientRolesError('تعذّر تحميل قائمة الأدوار. حاول تحديث الصفحة.');
      });
    return () => ctrl.abort();
  }, [me]);

  if (me?.system_admin) {
    return (
      <NotFoundState
        title="لا توجد قائمة عملاء"
        description="System admin لا يملك سياق وكالة. هذه الصفحة مخصّصة للمستخدمين التابعين لمؤسسة فعّالة."
      />
    );
  }

  function openForm() {
    setShowForm(true);
    if (params.get('new') !== '1') {
      const next = new URLSearchParams(params);
      next.set('new', '1');
      setParams(next, { replace: true });
    }
  }

  function closeForm() {
    setShowForm(false);
    if (params.get('new') === '1') {
      const next = new URLSearchParams(params);
      next.delete('new');
      setParams(next, { replace: true });
    }
  }

  function handleCreated(client: ClientRow, inviteResultState: InviteResultState | null) {
    // Task 112D — prepend the new row optimistically AND surface the
    // invite-result banner. Auto-navigation was removed so the
    // operator can read the banner (especially the copy-link
    // fallback) before drilling into the workspace; a clear "افتح
    // مساحة العميل" link inside the banner replaces the implicit
    // navigation.
    setState((prev) =>
      prev.status === 'ready' ? { status: 'ready', clients: [client, ...prev.clients] } : prev,
    );
    clientScope.refresh(client);
    closeForm();
    if (inviteResultState) setInviteResult(inviteResultState);
  }

  function handleRowInviteSent(client: ClientRow, inviteResultState: InviteResultState) {
    setInviteResult(inviteResultState);
    setInviteModalClient(null);
  }

  return (
    <div className="stack-md">
      <div
        className="page-head"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
        }}
      >
        <div>
          <h1 className="h-page">
            العملاء{' '}
            {state.status === 'ready' ? (
              <span
                className="serif italic"
                style={{ color: 'var(--tangerine)', fontSize: '0.65em' }}
              >
                — {state.clients.length} عميل
              </span>
            ) : null}
          </h1>
          <p className="sub">
            جميع العملاء التي تملك وصولاً إليها. اضغط أي عميل لفتح workspace كامل (9 تابات).
          </p>
        </div>
        {canCreate && !showForm ? (
          <button type="button" className="auth-cta" onClick={openForm} style={{ width: 'auto' }}>
            <span>إضافة عميل</span>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        ) : null}
      </div>

      {inviteResult ? (
        <InviteResultBanner
          state={inviteResult}
          onDismiss={() => setInviteResult(null)}
          onOpenClient={(name) => {
            const target =
              state.status === 'ready' ? state.clients.find((c) => c.name === name) : undefined;
            if (target) navigate(`/app/clients/${target.id}/overview`);
          }}
        />
      ) : null}

      {clientRolesError ? (
        <div className="banner banner-error" role="alert">
          {clientRolesError}
        </div>
      ) : null}

      {canCreate && showForm ? (
        <CreateClientForm onClose={closeForm} onCreated={handleCreated} clientRoles={clientRoles} />
      ) : null}

      <div className="toolbar-row">
        <FilterChip current={filter} value="all" label="الكل" onClick={setFilter} />
        <FilterChip current={filter} value="active" label="نشط" onClick={setFilter} />
        <FilterChip current={filter} value="onboarding" label="تأهيل" onClick={setFilter} />
        <FilterChip current={filter} value="paused" label="متوقف" onClick={setFilter} />
        <FilterChip current={filter} value="archived" label="مؤرشف" onClick={setFilter} />
      </div>

      {state.status === 'loading' ? <LoadingState title="جاري تحميل قائمة العملاء…" /> : null}
      {state.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل قائمة العملاء"
          error={state.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}
      {state.status === 'not-found' ? (
        <NotFoundState
          title="لا يوجد سياق وكالة"
          description="لا يمكن جلب قائمة العملاء لهذا الحساب."
        />
      ) : null}
      {state.status === 'ready' ? (
        state.clients.length === 0 ? (
          canCreate ? (
            <EmptyState
              title="لا يوجد عملاء بعد"
              description="ابدأ بإضافة أول عميل لتفعيل مساحة العمل."
              action={
                showForm ? undefined : (
                  <button type="button" className="auth-cta" onClick={openForm}>
                    <span>إضافة أول عميل</span>
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </button>
                )
              }
            />
          ) : (
            <EmptyState
              title="لا يوجد عملاء بعد"
              description="ستظهر هنا قائمة العملاء بمجرد ربط أول عميل بمساحة الوكالة."
            />
          )
        ) : (
          <ClientsGrid
            clients={state.clients}
            filter={filter}
            productionWorkspace={productionWorkspace}
            onInvite={(client) => setInviteModalClient(client)}
          />
        )
      ) : null}

      {inviteModalClient ? (
        <InviteUserModal
          client={inviteModalClient}
          clientRoles={clientRoles}
          onClose={() => setInviteModalClient(null)}
          onSent={handleRowInviteSent}
        />
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Task 112D — Invite-result banner (post-create + per-row outcome)
// ──────────────────────────────────────────────────────────────────────

function InviteResultBanner({
  state,
  onDismiss,
  onOpenClient,
}: {
  readonly state: InviteResultState;
  readonly onDismiss: () => void;
  readonly onOpenClient: (clientName: string) => void;
}) {
  const sent = state.delivery === 'sent';
  const headline = sent
    ? 'تم إنشاء العميل وإرسال دعوة البوابة'
    : 'تم إنشاء العميل، لكن تعذّر إرسال البريد. انسخ رابط الدعوة وأرسله يدوياً.';
  const klass = sent ? 'banner banner-info' : 'banner banner-error';

  return (
    <div
      className={klass}
      role={sent ? 'status' : 'alert'}
      style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <strong>{headline}</strong>
        <button
          type="button"
          className="btn-text"
          onClick={onDismiss}
          aria-label="إغلاق التنبيه"
          style={{ padding: '0 8px' }}
        >
          إخفاء
        </button>
      </div>
      <div className="tiny" style={{ opacity: 0.85 }}>
        {`الدعوة موجّهة إلى ${state.email} لعميل ${state.clientName}.`}
      </div>
      {/* Codex N1 fix — only render the copy-link panel when we
          actually have a URL to copy. The acceptUrl is empty when
          `sendUserInvitation` itself threw before returning a
          raw_token (network down, JWT expired, edge 5xx). In that
          case the row-level "دعوة مستخدم عميل" action is the
          recovery path; show that hint instead of an empty <code>. */}
      {!sent && state.acceptUrl.length > 0 ? <CopyInviteLink url={state.acceptUrl} /> : null}
      {!sent && state.acceptUrl.length === 0 ? (
        <div className="tiny" style={{ color: 'var(--rose-ink)' }}>
          أعد المحاولة من زر "دعوة مستخدم عميل" على بطاقة العميل لإصدار رابط دعوة جديد.
        </div>
      ) : null}
      <div>
        <button
          type="button"
          className="btn-text"
          onClick={() => onOpenClient(state.clientName)}
          style={{ padding: '4px 8px' }}
        >
          افتح مساحة العميل ←
        </button>
      </div>
    </div>
  );
}

function CopyInviteLink({ url }: { readonly url: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }
  return (
    <div className="card" style={{ padding: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
      <code
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 12,
          direction: 'ltr',
        }}
        aria-label="رابط الدعوة لمرة واحدة"
      >
        {url}
      </code>
      <button type="button" className="btn" onClick={() => void copy()} style={{ flexShrink: 0 }}>
        {copied ? 'تم النسخ' : 'نسخ الرابط'}
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Task 043 + Task 112D — Create-client form with required client email
// ──────────────────────────────────────────────────────────────────────

function CreateClientForm({
  onClose,
  onCreated,
  clientRoles,
}: {
  readonly onClose: () => void;
  readonly onCreated: (client: ClientRow, inviteResult: InviteResultState | null) => void;
  readonly clientRoles: ReadonlyArray<RoleRow> | null;
}) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<ClientStatus>('onboarding');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const nextErrors: Record<string, string[]> = {};
    if (trimmedName.length === 0) {
      nextErrors['name'] = ['اسم العميل مطلوب.'];
    } else if (trimmedName.length > 120) {
      nextErrors['name'] = ['اسم العميل يجب أن يكون 120 حرفاً أو أقل.'];
    }
    if (trimmedEmail.length === 0) {
      nextErrors['email'] = ['البريد الإلكتروني للعميل مطلوب لإنشاء بوابة العميل.'];
    } else if (!EMAIL_RE.test(trimmedEmail)) {
      nextErrors['email'] = ['صيغة البريد الإلكتروني غير صحيحة.'];
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }
    if (!clientRoles) {
      setError('قائمة الأدوار لم تُحمَّل بعد. حاول مجدداً خلال لحظات.');
      return;
    }
    const role = resolveDefaultClientRole(clientRoles);
    if (!role) {
      setError(
        'لا يوجد دور عميل مناسب (client_admin أو client_manager) في هذه المؤسسة. تواصل مع Owner لإنشاء الدور قبل الإكمال.',
      );
      return;
    }
    setSubmitting(true);
    let createdClient: ClientRow;
    try {
      const result = await createClientApi({
        input: {
          name: trimmedName,
          status,
          notes: notes.trim().length > 0 ? notes.trim() : null,
        },
      });
      createdClient = result.client;
    } catch (err) {
      setSubmitting(false);
      handleCreateClientError(err, setError, setFieldErrors);
      return;
    }
    let inviteResult: InviteResultState | null = null;
    try {
      const inviteResp = await sendUserInvitation({
        input: {
          email: trimmedEmail,
          role_id: role.id,
          client_id: createdClient.id,
        },
      });
      inviteResult = inviteResultFromResponse(inviteResp, createdClient.name, trimmedEmail);
    } catch (err) {
      // Task 112D — invitation failure must NOT discard the client
      // (the create succeeded). Surface a banner-level message so the
      // operator can retry the invite from the row action.
      inviteResult = {
        clientName: createdClient.name,
        email: trimmedEmail,
        delivery: 'failed',
        acceptUrl: '',
      };
      const message =
        err instanceof KmiApiError
          ? `تعذّر إرسال الدعوة (${err.code}). أعد المحاولة من بطاقة العميل.`
          : 'تعذّر إرسال الدعوة. أعد المحاولة من بطاقة العميل.';
      setError(message);
    }
    onCreated(createdClient, inviteResult);
  }

  const nameErr = fieldErrors['name']?.[0];
  const emailErr = fieldErrors['email']?.[0];

  return (
    <div
      className="card"
      style={{
        padding: 20,
        background: 'var(--cream-2)',
        border: '1px solid var(--line-2)',
      }}
    >
      <div className="sec-title">
        <h2>إضافة عميل جديد</h2>
        <button
          type="button"
          className="btn-text"
          onClick={onClose}
          style={{ marginInlineStart: 'auto', padding: 6 }}
          disabled={submitting}
        >
          إلغاء
        </button>
      </div>
      <form onSubmit={onSubmit} noValidate style={{ marginTop: 12 }}>
        {error ? (
          <div className="banner banner-error" style={{ marginBottom: 14 }} role="alert">
            {error}
          </div>
        ) : null}

        <div className="auth-field">
          <label className="auth-field-label" htmlFor="client_name">
            اسم العميل
          </label>
          <div className="auth-input-wrap">
            <input
              ref={nameRef}
              id="client_name"
              className="auth-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="مثال: مطاعم السعد"
              disabled={submitting}
              aria-invalid={nameErr ? true : undefined}
              aria-describedby={nameErr ? 'client_name_error' : undefined}
              maxLength={120}
              style={{ paddingInlineStart: 18 }}
            />
          </div>
          {nameErr ? (
            <div
              id="client_name_error"
              className="tiny"
              style={{ color: 'var(--rose)', marginTop: 4 }}
            >
              {nameErr}
            </div>
          ) : null}
        </div>

        <div className="auth-field">
          <label className="auth-field-label" htmlFor="client_email">
            البريد الإلكتروني للعميل
          </label>
          <div className="auth-input-wrap">
            <input
              id="client_email"
              className="auth-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              placeholder="client@example.com"
              disabled={submitting}
              required
              aria-required="true"
              aria-invalid={emailErr ? true : undefined}
              aria-describedby={emailErr ? 'client_email_error' : 'client_email_hint'}
              maxLength={320}
              style={{ paddingInlineStart: 18, direction: 'ltr', textAlign: 'left' }}
            />
          </div>
          {emailErr ? (
            <div
              id="client_email_error"
              className="tiny"
              style={{ color: 'var(--rose)', marginTop: 4 }}
            >
              {emailErr}
            </div>
          ) : (
            <div
              id="client_email_hint"
              className="tiny"
              style={{ color: 'var(--muted)', marginTop: 4 }}
            >
              سترسل دعوة إلى هذا البريد لإنشاء حساب بوابة العميل.
            </div>
          )}
        </div>

        <div className="auth-field">
          <label className="auth-field-label" htmlFor="client_status">
            الحالة
          </label>
          <select
            id="client_status"
            className="auth-input"
            value={status}
            onChange={(e) => setStatus(e.currentTarget.value as ClientStatus)}
            disabled={submitting}
            style={{ paddingInlineStart: 18 }}
          >
            <option value="onboarding">تأهيل</option>
            <option value="active">نشط</option>
            <option value="paused">متوقف</option>
            <option value="archived">مؤرشف</option>
          </select>
        </div>

        <div className="auth-field">
          <label className="auth-field-label" htmlFor="client_notes">
            ملاحظات (اختياري)
          </label>
          <textarea
            id="client_notes"
            className="auth-input"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            placeholder="أي تفاصيل داخلية تخص بداية الحساب."
            disabled={submitting}
            rows={3}
            maxLength={1000}
            style={{ paddingInlineStart: 18, resize: 'vertical' }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button
            type="submit"
            className="auth-cta"
            disabled={submitting || !clientRoles}
            style={{ width: 'auto' }}
          >
            {submitting ? <span className="spinner" /> : null}
            <span>{submitting ? 'جاري الإنشاء…' : 'إنشاء العميل وإرسال الدعوة'}</span>
          </button>
          <button
            type="button"
            className="btn-text"
            onClick={onClose}
            disabled={submitting}
            style={{ padding: '10px 14px' }}
          >
            إلغاء
          </button>
        </div>
      </form>
    </div>
  );
}

function handleCreateClientError(
  err: unknown,
  setError: (msg: string | null) => void,
  setFieldErrors: (next: Record<string, string[]>) => void,
): void {
  if (err instanceof KmiApiError) {
    if (err.code === 'conflict') {
      setFieldErrors({ name: ['اسم العميل مستخدم بالفعل في مؤسستك.'] });
      return;
    }
    if (err.code === 'permission_denied') {
      setError('لا تملك صلاحية إضافة عملاء. تواصل مع Owner الوكالة.');
      return;
    }
    // Task 100 — client creation blocked by the client.limit gate. The
    // EF returns 402 entitlement_denied with feature_key='client.limit'
    // for ALL of: cap reached, no active subscription, or custom plan
    // without an explicit limit override. From the reader's point of
    // view the next step is the same in every case, so a single Arabic
    // message covers all three. The archive-to-free-capacity lever
    // still applies whenever the cause is a reached cap, so it is kept
    // as a secondary hint.
    //
    // TASK 300A. `client.limit` denial has THREE causes and the wire
    // carries no discriminator: the cap is reached, the subscription
    // is not active, or a custom plan has no configured limit.
    // Standing is NOT a fourth here — `create-client` calls
    // `requireOperationalOrganization` BEFORE the limit check, so a
    // non-operational organization gets `commercial_activation_required`
    // and never reaches this branch. (The seat cap in
    // `AcceptInvitationPage` IS different: that route has no such
    // gate, so standing reaches it. Same shape of copy, different
    // cause list — which is why each was reasoned about separately.)
    //
    // So the lead states only what is true in all three — creation is
    // not available right now — and NAMES NO AXIS: two of the three
    // are not about the cap, and «change the plan» is not the remedy
    // for a subscription that is merely inactive. Each lever is
    // conditional on the cause it addresses, and the fallback asks
    // Super Agency to CHECK rather than prescribing a change
    // (CLP-1.2 — there is no self-serve upgrade in any case).
    if (err.code === 'entitlement_denied' && err.body.feature_key === 'client.limit') {
      setError(
        'تعذّر إنشاء عميل جديد حالياً. إن كان السبب بلوغ الحد الأقصى للعملاء فيمكنك أرشفة عميل غير نشط لإتاحة مكان؛ وإلا فتواصل مع فريق Super Agency للتحقق من حالة حساب الوكالة أو إعداد خطتها.',
      );
      return;
    }
    if (err.code === 'validation_error') {
      if (err.body.fields) {
        const next: Record<string, string[]> = {};
        for (const [k, v] of Object.entries(err.body.fields)) next[k] = [...v];
        setFieldErrors(next);
        return;
      }
      setError('البيانات غير صالحة. تأكد من الحقول وحاول مجدداً.');
      return;
    }
    if (err.code === 'unauthenticated') {
      setError('انتهت الجلسة. سيتم إعادة تسجيل الدخول…');
      return;
    }
  }
  setError('تعذّر إنشاء العميل. حاول مجدداً.');
}

// ──────────────────────────────────────────────────────────────────────
// Task 112D — Per-row "دعوة مستخدم عميل" modal for existing clients
// ──────────────────────────────────────────────────────────────────────

function InviteUserModal({
  client,
  clientRoles,
  onClose,
  onSent,
}: {
  readonly client: ClientRow;
  readonly clientRoles: ReadonlyArray<RoleRow> | null;
  readonly onClose: () => void;
  readonly onSent: (client: ClientRow, inviteResult: InviteResultState) => void;
}) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setEmailError(null);
    const trimmed = email.trim().toLowerCase();
    if (trimmed.length === 0) {
      setEmailError('البريد الإلكتروني مطلوب.');
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError('صيغة البريد الإلكتروني غير صحيحة.');
      return;
    }
    if (!clientRoles) {
      setError('قائمة الأدوار لم تُحمَّل بعد. حاول مجدداً خلال لحظات.');
      return;
    }
    const role = resolveDefaultClientRole(clientRoles);
    if (!role) {
      setError(
        'لا يوجد دور عميل مناسب (client_admin أو client_manager) في هذه المؤسسة. تواصل مع Owner لإنشاء الدور قبل الإكمال.',
      );
      return;
    }
    setSubmitting(true);
    try {
      const resp = await sendUserInvitation({
        input: { email: trimmed, role_id: role.id, client_id: client.id },
      });
      onSent(client, inviteResultFromResponse(resp, client.name, trimmed));
    } catch (err) {
      setSubmitting(false);
      if (err instanceof KmiApiError) {
        if (err.code === 'conflict') {
          setError('يوجد دعوة معلّقة بهذا البريد بالفعل لهذا العميل.');
          return;
        }
        if (err.code === 'permission_denied') {
          setError('لا تملك صلاحية دعوة مستخدمي العملاء.');
          return;
        }
        if (err.code === 'validation_error') {
          setError('البيانات غير صالحة. تأكد من البريد الإلكتروني.');
          return;
        }
      }
      setError('تعذّر إرسال الدعوة. حاول مجدداً.');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`دعوة مستخدم عميل لعميل ${client.name}`}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20, 16, 10, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="card" style={{ padding: 22, maxWidth: 460, width: '100%' }}>
        <div className="sec-title">
          <h2>دعوة مستخدم عميل</h2>
        </div>
        <p className="tiny" style={{ marginTop: 4, color: 'var(--muted)' }}>
          {`سيتم إرسال دعوة بوابة لعميل ${client.name}.`}
        </p>
        <form onSubmit={onSubmit} noValidate style={{ marginTop: 12 }}>
          {error ? (
            <div className="banner banner-error" role="alert" style={{ marginBottom: 12 }}>
              {error}
            </div>
          ) : null}
          <div className="auth-field">
            <label className="auth-field-label" htmlFor="invite_email">
              البريد الإلكتروني للعميل
            </label>
            <div className="auth-input-wrap">
              <input
                ref={inputRef}
                id="invite_email"
                className="auth-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                placeholder="client@example.com"
                disabled={submitting}
                required
                aria-required="true"
                aria-invalid={emailError ? true : undefined}
                aria-describedby={emailError ? 'invite_email_error' : undefined}
                maxLength={320}
                style={{ paddingInlineStart: 18, direction: 'ltr', textAlign: 'left' }}
              />
            </div>
            {emailError ? (
              <div
                id="invite_email_error"
                className="tiny"
                style={{ color: 'var(--rose)', marginTop: 4 }}
              >
                {emailError}
              </div>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn-text"
              onClick={onClose}
              disabled={submitting}
              style={{ padding: '10px 14px' }}
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="auth-cta"
              disabled={submitting || !clientRoles}
              style={{ width: 'auto' }}
            >
              {submitting ? <span className="spinner" /> : null}
              <span>{submitting ? 'جارٍ الإرسال…' : 'إرسال الدعوة'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FilterChip({
  current,
  value,
  label,
  onClick,
}: {
  readonly current: ClientStatus | 'all';
  readonly value: ClientStatus | 'all';
  readonly label: string;
  readonly onClick: (v: ClientStatus | 'all') => void;
}) {
  return (
    <button
      type="button"
      className={current === value ? 'chip on' : 'chip'}
      onClick={() => onClick(value)}
    >
      {label}
    </button>
  );
}

function ClientsGrid({
  clients,
  filter,
  productionWorkspace,
  onInvite,
}: {
  readonly clients: ReadonlyArray<ClientRow>;
  readonly filter: ClientStatus | 'all';
  readonly productionWorkspace: boolean;
  readonly onInvite: (client: ClientRow) => void;
}) {
  const filtered = filter === 'all' ? clients : clients.filter((c) => c.status === filter);
  if (filtered.length === 0) {
    return <EmptyState title="لا نتائج" description="جرّب فلتر آخر، أو أعد تعيين الفلاتر." />;
  }
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))',
        gap: 14,
      }}
    >
      {filtered.map((c, i) => {
        const accents = ['t1', 't2', 't3', 't4', 't5', 't6'];
        const accent = accents[i % accents.length];
        return (
          <ClientCard
            key={c.id}
            client={c}
            accent={accent ?? 't1'}
            productionWorkspace={productionWorkspace}
            onInvite={() => onInvite(c)}
          />
        );
      })}
    </div>
  );
}

function ClientCard({
  client,
  accent,
  productionWorkspace,
  onInvite,
}: {
  readonly client: ClientRow;
  readonly accent: string;
  readonly productionWorkspace: boolean;
  readonly onInvite: () => void;
}) {
  const accentBg =
    accent === 't1'
      ? 'var(--tangerine-soft)'
      : accent === 't2'
        ? 'var(--sage-soft)'
        : accent === 't3'
          ? 'var(--sky-soft)'
          : accent === 't4'
            ? 'var(--lilac-soft)'
            : accent === 't5'
              ? 'var(--amber-soft)'
              : 'var(--pink-soft)';
  const accentInk =
    accent === 't1'
      ? 'var(--tangerine-ink)'
      : accent === 't2'
        ? 'var(--sage-ink)'
        : accent === 't3'
          ? 'var(--sky-ink)'
          : accent === 't4'
            ? 'var(--plum)'
            : accent === 't5'
              ? 'var(--amber-ink)'
              : '#A03460';

  // Task 112D — the card body remains a Link (preserves the
  // existing affordance of "click anywhere on the card to open the
  // workspace"), but the per-row action row sits OUTSIDE the link
  // so the "دعوة مستخدم عميل" button never accidentally navigates.
  //
  // Task 101 — archived clients are read-only on the list view. We
  // render the same visual chrome (initials, name, updated_at, status
  // pill) but:
  //   - the wrapper is a <div>, not a <Link> (no nav to a workspace
  //     that would dead-end at NotFoundState per PR #67/#68 anyway);
  //   - the footer label is "مؤرشف — للقراءة فقط" instead of "فتح →";
  //   - the per-row "دعوة مستخدم عميل" button is hidden entirely
  //     (sending an invite scoped to an archived client is meaningless).
  // Active / onboarding / paused cards keep their existing affordances
  // exactly — the branch below is the ONLY divergence.
  const isArchived = client.status === 'archived';

  const cardBodyContent = (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 10,
            background: accentBg,
            color: accentInk,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--font-serif)',
            fontWeight: 600,
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {clientInitials(client.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 className="h-card" style={{ fontSize: 15 }}>
            {client.name}
          </h3>
          <div className="tiny" style={{ marginTop: 3 }}>
            مُحدّث {new Date(client.updated_at).toLocaleDateString('ar-SA')}
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 10,
          borderTop: '1px dashed var(--line-2)',
        }}
      >
        <span className={STATUS_PILL[client.status]}>{STATUS_LABEL[client.status]}</span>
        <span className="tiny" style={{ color: 'var(--muted)' }}>
          {isArchived ? 'مؤرشف — للقراءة فقط' : 'فتح →'}
        </span>
      </div>
    </>
  );

  return (
    <div
      className="card"
      style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      {isArchived ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            color: 'inherit',
          }}
        >
          {cardBodyContent}
        </div>
      ) : (
        <Link
          to={
            productionWorkspace
              ? `/app?client=${encodeURIComponent(client.id)}`
              : `/app/clients/${client.id}/overview`
          }
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            color: 'inherit',
            textDecoration: 'none',
          }}
        >
          {cardBodyContent}
        </Link>
      )}
      {isArchived ? null : (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn-text"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              e.stopPropagation();
              onInvite();
            }}
            style={{ padding: '6px 8px', fontSize: 12 }}
          >
            دعوة مستخدم عميل
          </button>
        </div>
      )}
    </div>
  );
}
